/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { Tooltip } from 'antd'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import { TabItem } from '../hooks/useRegistrationSetupTabs'
import React from 'react'

type SidebarNavProps = {
  tabs: TabItem[]
  activeTab: string
  onTabChange: (key: string) => void
}

const SIDEBAR_WIDTH = 250

const sidebarContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  padding-right: 16px;
`

const tabItemStyle = (isSelected: boolean, isDisabled: boolean) => css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  opacity: ${isDisabled ? 0.5 : 1};
  background-color: ${isSelected ? colors.SECONDARY_300 : colors.SECONDARY_500};
  position: relative;
  transition: background-color 0.2s ease;

  ${!isDisabled &&
  !isSelected &&
  `
    &:hover {
      background-color: #212935;
    }
  `}
`

const skeletonPulse = css`
  @keyframes sidebarSkeletonPulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.7;
    }
  }
`

// Mirrors tabItemStyle's box so the row occupies the same space as the real tab.
const skeletonRowStyle = css`
  ${skeletonPulse}
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_500};
`

const skeletonBlockStyle = (width: string, height: string) => css`
  width: ${width};
  height: ${height};
  border-radius: 4px;
  background-color: ${colors.SECONDARY_300};
  animation: sidebarSkeletonPulse 1.4s ease-in-out infinite;
`

const accentBarStyle = css`
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 4px;
  border-radius: 2px;
  background-color: ${colors.PRIMARY};
`

const WaiverTooltipContent = () => (
  <Text variant='paragraphSmall' weight='regular' color={colors.NEUTRAL_0}>
    Waiver(s) are required for signature during registration <br /> New waivers
    can be added via the{' '}
    <Link
      to={`${ROUTES.GENERAL_SETTINGS}?origin=waiver`}
      css={css`
        color: ${colors.PRIMARY};
        cursor: pointer;
        text-decoration: underline;
        &:hover {
          opacity: 0.8;
        }
      `}
      target='_blank'
    >
      Account Settings
    </Link>
  </Text>
)

const SidebarNav = ({ tabs, activeTab, onTabChange }: SidebarNavProps) => {
  return (
    <div css={sidebarContainerStyle}>
      {tabs.map((tab) => {
        if (tab.loading) {
          return (
            <div key={tab.key} css={skeletonRowStyle} aria-hidden='true'>
              <div css={skeletonBlockStyle('20px', '20px')} />
              <div css={skeletonBlockStyle('96px', '14px')} />
            </div>
          )
        }

        const isSelected = activeTab === tab.key
        const Icon = tab.icon

        const tabElement = (
          <div
            key={tab.key}
            css={tabItemStyle(isSelected, tab.disabled)}
            onClick={() => {
              if (!tab.disabled) {
                onTabChange(tab.key)
              }
            }}
          >
            {isSelected && <div css={accentBarStyle} />}
            <Icon
              css={css`
                font-size: 20px;
                color: ${isSelected ? colors.PRIMARY : colors.WHITE};
              `}
            />
            <Text
              variant='paragraphSmall'
              weight={isSelected ? 'medium' : 'regular'}
              color={isSelected ? colors.PRIMARY : colors.WHITE}
            >
              {tab.label}
            </Text>
          </div>
        )

        if (tab.disabled && tab.key === '2') {
          return (
            <Tooltip
              key={tab.key}
              title={<WaiverTooltipContent />}
              zIndex={10006}
              placement='right'
            >
              {tabElement}
            </Tooltip>
          )
        }

        return <React.Fragment key={tab.key}>{tabElement}</React.Fragment>
      })}
    </div>
  )
}

// Mobile horizontal tabs
const mobileContainerStyle = css`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const mobileTabStyle = (isSelected: boolean, isDisabled: boolean) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  opacity: ${isDisabled ? 0.5 : 1};
  background-color: ${isSelected ? colors.SECONDARY_300 : colors.SECONDARY_500};
  white-space: nowrap;
  flex-shrink: 0;
  ${isSelected ? `border-bottom: 2px solid ${colors.PRIMARY};` : ''}
`

// Mirrors mobileTabStyle's box so the row occupies the same space.
const mobileSkeletonRowStyle = css`
  ${skeletonPulse}
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_500};
  white-space: nowrap;
  flex-shrink: 0;
`

export const MobileSidebarNav = ({
  tabs,
  activeTab,
  onTabChange,
}: SidebarNavProps) => {
  return (
    <div css={mobileContainerStyle}>
      {tabs.map((tab) => {
        if (tab.loading) {
          return (
            <div key={tab.key} css={mobileSkeletonRowStyle} aria-hidden='true'>
              <div css={skeletonBlockStyle('18px', '18px')} />
              <div css={skeletonBlockStyle('80px', '14px')} />
            </div>
          )
        }

        const isSelected = activeTab === tab.key
        const Icon = tab.icon

        const tabElement = (
          <div
            key={tab.key}
            css={mobileTabStyle(isSelected, tab.disabled)}
            onClick={() => {
              if (!tab.disabled) {
                onTabChange(tab.key)
              }
            }}
          >
            <Icon
              css={css`
                font-size: 18px;
                color: ${isSelected ? colors.PRIMARY : colors.WHITE};
              `}
            />
            <Text
              variant='paragraphSmall'
              weight={isSelected ? 'medium' : 'regular'}
              color={isSelected ? colors.PRIMARY : colors.WHITE}
            >
              {tab.label}
            </Text>
          </div>
        )

        if (tab.disabled && tab.key === '2') {
          return (
            <Tooltip
              key={tab.key}
              title={<WaiverTooltipContent />}
              zIndex={10006}
              placement='bottom'
            >
              {tabElement}
            </Tooltip>
          )
        }

        return <React.Fragment key={tab.key}>{tabElement}</React.Fragment>
      })}
    </div>
  )
}

export default SidebarNav
